import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Link, router } from '@inertiajs/react'
import { Edit, Trash2, Eye, Award, Users, ThumbsUp, Vote, Lock } from 'lucide-react'

interface Contest {
  id: number
  title: string
  description: string
  theme: string
  status: string
  status_label: string
  community_name: string
  submission_start_date: string
  submission_end_date: string
  voting_end_date: string
  participation_points: number
  vote_points: number
  first_place_points: number
  second_place_points: number
  third_place_points: number
  published: boolean
  active: boolean
  accepting_submissions: boolean
  accepting_votes: boolean
  total_submissions: number
  approved_submissions: number
  pending_moderation: number
  total_votes: number
}

interface Stats {
  total_submissions: number
  approved_submissions: number
  pending_moderation: number
  total_votes: number
}

interface ShowProps {
  contest: Contest
  stats: Stats
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submissions_open: 'bg-green-100 text-green-800',
  voting_open: 'bg-blue-100 text-blue-800',
  closed: 'bg-red-100 text-red-800',
  winners_declared: 'bg-purple-100 text-purple-800'
}

export default function Show({ contest, stats }: ShowProps) {
  const handlePublish = () => {
    if (confirm('Publier ce concours ?')) {
      router.post(`/admin/photo_contests/${contest.id}/publish`)
    }
  }

  const handleOpenVoting = () => {
    if (confirm('Ouvrir la phase de votes ?')) {
      router.post(`/admin/photo_contests/${contest.id}/open_voting`)
    }
  }

  const handleCloseContest = () => {
    if (confirm('Clôturer le concours ?')) {
      router.post(`/admin/photo_contests/${contest.id}/close_contest`)
    }
  }

  const handleDeclareWinners = () => {
    if (confirm('Déclarer les gagnants ? Cette action est irréversible.')) {
      router.post(`/admin/photo_contests/${contest.id}/declare_winners`)
    }
  }

  const handleDelete = () => {
    if (confirm(`Supprimer "${contest.title}" ?`)) {
      router.delete(`/admin/photo_contests/${contest.id}`)
    }
  }

  return (
    <AdminLayout
      title={contest.title}
      breadcrumbs={[
        { label: 'Concours Photo', href: '/admin/photo_contests' },
        { label: contest.title }
      ]}
    >
      {/* Header */}
      <Card className="p-6 mb-6">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-text-dark mb-3">{contest.title}</h1>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <Badge className={statusColors[contest.status]}>{contest.status_label}</Badge>
              {contest.theme && <span className="text-sm text-text-gray">Thème: {contest.theme}</span>}
              <span className="text-sm text-text-gray">{contest.community_name}</span>
            </div>
            {contest.description && (
              <p className="text-text-gray">{contest.description}</p>
            )}
          </div>
          <div className="flex gap-2">
            {!contest.published && (
              <Button onClick={handlePublish} className="flex items-center gap-2">
                <Eye size={18} />
                Publier
              </Button>
            )}
            {contest.status === 'submissions_open' && (
              <Button onClick={handleOpenVoting} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
                <Vote size={18} />
                Ouvrir les votes
              </Button>
            )}
            {contest.status === 'voting_open' && (
              <Button onClick={handleCloseContest} className="flex items-center gap-2 bg-red-600 hover:bg-red-700">
                <Lock size={18} />
                Clôturer
              </Button>
            )}
            {contest.status === 'closed' && (
              <Button onClick={handleDeclareWinners} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700">
                <Award size={18} />
                Déclarer Gagnants
              </Button>
            )}
            <Link href={`/admin/photo_contests/${contest.id}/edit`}>
              <Button variant="secondary" className="flex items-center gap-2">
                <Edit size={18} />
                Modifier
              </Button>
            </Link>
            <Button
              variant="secondary"
              className="text-red-600 hover:bg-red-50"
              onClick={handleDelete}
            >
              <Trash2 size={18} />
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Users size={18} className="text-gray-400" />
            <p className="text-sm text-text-gray">Total Soumissions</p>
          </div>
          <p className="text-2xl font-bold">{stats.total_submissions}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Award size={18} className="text-green-600" />
            <p className="text-sm text-text-gray">Approuvées</p>
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.approved_submissions}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <Eye size={18} className="text-orange-600" />
            <p className="text-sm text-text-gray">En attente</p>
          </div>
          <p className="text-2xl font-bold text-orange-600">{stats.pending_moderation}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <ThumbsUp size={18} className="text-blue-600" />
            <p className="text-sm text-text-gray">Total Votes</p>
          </div>
          <p className="text-2xl font-bold text-blue-600">{stats.total_votes}</p>
        </Card>
      </div>

      {/* Dates Info */}
      <Card className="p-6 mb-6">
        <h2 className="text-lg font-semibold text-text-dark mb-4">Calendrier</h2>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-text-gray mb-1">Soumissions</p>
            <p className="text-sm font-medium">
              {new Date(contest.submission_start_date).toLocaleDateString('fr-FR')}
              {' → '}
              {new Date(contest.submission_end_date).toLocaleDateString('fr-FR')}
            </p>
            {contest.accepting_submissions && (
              <Badge className="bg-green-100 text-green-800 mt-1">En cours</Badge>
            )}
          </div>
          <div>
            <p className="text-sm text-text-gray mb-1">Votes</p>
            <p className="text-sm font-medium">
              {new Date(contest.submission_end_date).toLocaleDateString('fr-FR')}
              {' → '}
              {new Date(contest.voting_end_date).toLocaleDateString('fr-FR')}
            </p>
            {contest.accepting_votes && (
              <Badge className="bg-blue-100 text-blue-800 mt-1">En cours</Badge>
            )}
          </div>
          <div>
            <p className="text-sm text-text-gray mb-1">Points</p>
            <div className="text-sm">
              <div>Participation: {contest.participation_points} pts</div>
              <div>Vote: {contest.vote_points} pts</div>
              <div>Gagnants: {contest.first_place_points}/{contest.second_place_points}/{contest.third_place_points}</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Submissions Link */}
      {stats.total_submissions > 0 && (
        <Card className="p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-text-dark mb-1">Soumissions</h2>
              <p className="text-sm text-text-gray">
                Gérer les {stats.total_submissions} soumissions et modérer
              </p>
            </div>
            <Link href={`/admin/photo_contests/${contest.id}/submissions`}>
              <Button>Voir les soumissions</Button>
            </Link>
          </div>
        </Card>
      )}
    </AdminLayout>
  )
}
